import { useCallback, useEffect, useState } from 'react';
import {
  compareCustomers,
  listMerges,
  mergeCustomers,
  splitCustomers,
  type ComparePayload,
  type MergeHistoryRow,
} from '../api';
import { type IdentityLocale, type IdentityTranslate } from '../i18n';
import {
  Button,
  Card,
  DataTable,
  Drawer,
  Field,
  Id,
  Notice,
  StatusBadge,
  TextInput,
  type Column,
} from '../../../shell/kit';
import { CustomerPicker } from '../../../shell/pickers';
import { useToast } from '../../../shell/toast';
import { useConfirmReason } from '../../../shell/confirm';
import { formatMoment } from '../../../shell/format';
import { ComparePanel } from './compare-panel';
import { errorText } from '../../../shell/errors';

const MOVED_LABELS: Readonly<Record<string, string>> = {
  visit: 'визиты',
  consent: 'согласия',
  feedback: 'обращения',
  sales_order: 'заказы',
  subscription: 'подписки',
  customer_alias: 'контакты',
  customer_vehicle_link: 'машины',
  loyalty: 'бонусы',
  segments: 'сегменты',
};

function movedText(moved: Readonly<Record<string, number>>): string {
  const parts = Object.entries(moved).map(
    ([key, count]) => `${MOVED_LABELS[key] ?? key}: ${count}`,
  );
  return parts.length > 0 ? parts.join(', ') : '—';
}

export function MergeView({
  t,
  locale,
  allowed,
}: {
  t: IdentityTranslate;
  locale: IdentityLocale;
  allowed: boolean;
}) {
  const toast = useToast();
  const confirmReason = useConfirmReason();
  const [primaryId, setPrimaryId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [reason, setReason] = useState('');
  const [comparison, setComparison] = useState<ComparePayload | null>(null);
  const [merges, setMerges] = useState<readonly MergeHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fail = useCallback(
    (err: unknown) => {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    },
    [locale, toast],
  );

  const loadMerges = useCallback(async () => {
    setLoading(true);
    try {
      setMerges((await listMerges()).merges);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [fail]);

  useEffect(() => {
    if (allowed) void loadMerges();
  }, [allowed, loadMerges]);

  const doCompare = async (): Promise<void> => {
    try {
      setComparison(await compareCustomers(primaryId.trim(), sourceId.trim()));
    } catch (err) {
      fail(err);
    }
  };

  const doMerge = async (primary: string, source: string): Promise<void> => {
    if (reason.trim().length < 3) {
      toast.warn(t('identity.duplicates.needReason'));
      return;
    }
    setBusy(true);
    try {
      const result = await mergeCustomers(primary.trim(), source.trim(), reason);
      setComparison(null);
      setPrimaryId('');
      setSourceId('');
      setReason('');
      await loadMerges();
      // Идентификатор склейки больше не нужно откуда-то узнавать: он в журнале ниже.
      toast.ok(t('identity.merge.done', { mergeId: result.mergeId }));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const doSplit = async (row: MergeHistoryRow): Promise<void> => {
    // Основание — в том же диалоге, а не в нативном окне браузера (QA.md, Q25).
    const splitReason = await confirmReason({
      title: t('identity.split.confirmTitle'),
      body: t('identity.split.confirmBody', {
        primary: row.primaryName ?? row.primaryId,
        merged: row.mergedName ?? row.mergedId,
      }),
      consequences: [t('identity.split.consequence', { moved: movedText(row.moved) })],
      confirmLabel: t('identity.split.submit'),
      reasonLabel: t('identity.split.reasonPrompt'),
      reasonMin: 3,
    });
    if (!splitReason) return;

    setBusy(true);
    try {
      await splitCustomers(row.mergeId, splitReason.trim());
      await loadMerges();
      toast.ok(t('identity.split.done'));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const columns: ReadonlyArray<Column<MergeHistoryRow>> = [
    {
      key: 'when',
      header: t('identity.merges.when'),
      render: (row) => <span className="whitespace-nowrap">{formatMoment(row.mergedAt)}</span>,
    },
    {
      key: 'pair',
      header: t('identity.merges.pair'),
      render: (row) => (
        <div className="text-sm">
          <div className="text-ink">
            {row.primaryName ?? t('identity.duplicates.noName')}{' '}
            <span className="text-ink-muted">{row.primaryPhoneMasked ?? ''}</span>
          </div>
          <div className="text-ink-muted">
            ← {row.mergedName ?? t('identity.duplicates.noName')}{' '}
            {row.mergedPhoneMasked ?? ''}
          </div>
        </div>
      ),
    },
    {
      key: 'moved',
      header: t('identity.merges.moved'),
      render: (row) => <span className="text-xs text-ink-muted">{movedText(row.moved)}</span>,
    },
    { key: 'reason', header: t('identity.duplicates.reason'), render: (row) => row.reason },
    {
      key: 'state',
      header: t('identity.merges.state'),
      render: (row) =>
        row.canSplit ? (
          <StatusBadge tone="brand">{t('identity.merges.active')}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">
            {t('identity.merges.reverted', { at: formatMoment(row.revertedAt) })}
          </StatusBadge>
        ),
    },
    {
      key: 'id',
      header: t('identity.split.mergeId'),
      render: (row) => <Id value={row.mergeId} />,
    },
    {
      key: 'act',
      header: '',
      render: (row) =>
        row.canSplit ? (
          <Button disabled={busy} onClick={() => void doSplit(row)}>
            {t('identity.split.submit')}
          </Button>
        ) : null,
    },
  ];

  if (!allowed) return <Notice tone="warn">{t('identity.merge.onlyAdmin')}</Notice>;

  return (
    <div className="space-y-4">
      <Card title={t('identity.merge.title')}>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t('identity.merge.primary')} hint={t('identity.merge.primaryHint')}>
            <CustomerPicker value={primaryId} onChange={setPrimaryId} />
          </Field>
          <Field label={t('identity.merge.source')} hint={t('identity.merge.sourceHint')}>
            <CustomerPicker value={sourceId} onChange={setSourceId} />
          </Field>
        </div>
        <div className="mt-3">
          <Field
            label={t('identity.duplicates.reason')}
            hint={t('identity.duplicates.reasonHint')}
            required
          >
            <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={() => void doCompare()} disabled={!primaryId || !sourceId}>
            {t('identity.duplicates.compare')}
          </Button>
          <Button
            tone="primary"
            disabled={busy || !primaryId || !sourceId}
            onClick={() => void doMerge(primaryId, sourceId)}
          >
            {t('identity.merge.submit')}
          </Button>
        </div>
      </Card>

      {/* Журнал склеек — то, чего не было нигде. Без него разделение достижимо
          только запросом в базу, хотя фича заявлена как «разделить можно и через
          полгода». */}
      <Card title={t('identity.merges.title')} hint={t('identity.split.hint')}>
        <DataTable
          loading={loading}
          rows={merges}
          columns={columns}
          empty={t('identity.merges.empty')}
          emptyReason={t('identity.merges.emptyReason')}
          rowKey={(row) => row.mergeId}
        />
      </Card>

      {comparison ? (
        <Drawer title={t('identity.compare.title')} onClose={() => setComparison(null)}>
          <ComparePanel
            payload={comparison}
            t={t}
            onKeep={(primary, source) => void doMerge(primary, source)}
          />
        </Drawer>
      ) : null}
    </div>
  );
}
